import React from 'react';
import { useAudioVisualization } from '../../hooks/useAudioVisualization';
import MessageDeliveryStatus from '../MessageDeliveryStatus';

interface AudioMessageProps {
  message: {
    tempAudioUrl?: string;
    voice_duration?: number;
    created_at?: string | Date;
    status?: 'sending' | 'sent' | 'failed';
  };
  mediaUrl?: string;
  isUser: boolean;
  isProfileOpen: boolean;
}

export const AudioMessage: React.FC<AudioMessageProps> = ({
  message,
  mediaUrl,
  isUser,
  isProfileOpen
}) => {
  const { canvasRef, audioRef, setupAudioVisualization } = useAudioVisualization({ isUser });

  const containerClass = isUser ? 'flex justify-end pr-2' : 'flex justify-start pl-2';
  const bubbleClass = `
    relative 
    w-fit 
    max-w-[80%]
    px-4 py-3 
    rounded-xl 
    ${isUser 
      ? 'bg-cyan-500/10 border border-cyan-400/20 text-[var(--text-primary)]' 
      : 'bg-[var(--surface-elevated)] border border-[var(--border-color)] text-[var(--text-primary)]'
    }
    backdrop-blur-sm
    shadow-lg
    mb-2
    hover:border-cyan-400/30
    transition-all
  `;

  const audioClass = `
    w-[350px] mt-1 
    outline-none 
    ${isUser 
      ? 'bg-cyan-500/10 [&::-webkit-media-controls-panel]:bg-cyan-500/10' 
      : 'bg-[var(--surface-elevated)] [&::-webkit-media-controls-panel]:bg-[var(--surface-elevated)]'
    }
    [&::-webkit-media-controls-panel]:backdrop-blur-sm
    rounded-lg
    border
    ${isUser ? 'border-cyan-400/20' : 'border-[var(--border-color)]'}
  `;

  const renderTimestampAndStatus = () => {
    let displayTime = null;
    
    if (message.created_at) {
      const messageDate = new Date(message.created_at);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (messageDate.toDateString() === today.toDateString()) {
        displayTime = messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else if (messageDate.toDateString() === yesterday.toDateString()) {
        displayTime = `Yesterday, ${messageDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        })}`;
      } else {
        displayTime = messageDate.toLocaleDateString([], { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    }

    return (
      <div className="flex items-center justify-end mt-2 gap-1.5">
        {displayTime && (
          <span className="text-xs text-[var(--text-secondary)]">{displayTime}</span>
        )}
        {message.status && isUser && (
          <MessageDeliveryStatus 
            status={message.status as 'sending' | 'sent' | 'delivered' | 'read'}
            timestamp={new Date(message.created_at || new Date())}
          />
        )}
      </div>
    );
  };

  return (
    <div className={containerClass}>
      <div className={`${bubbleClass} ${isProfileOpen ? 'z-50' : ''}`}>
        <div className="flex flex-col">
          {(message.tempAudioUrl || mediaUrl) ? (
            <>
              <div className="relative group">
                <audio
                  ref={audioRef}
                  controls
                  src={message.tempAudioUrl || mediaUrl}
                  className={audioClass}
                  onPlay={() => {
                    if (audioRef.current && setupAudioVisualization) {
                      setupAudioVisualization(audioRef.current);
                    }
                  }}
                  onPause={() => {
                    // Animation is handled in the hook
                  }}
                />
                <canvas
                  ref={canvasRef}
                  width="350"
                  height="50"
                  className="absolute bottom-0 left-0 opacity-20 pointer-events-none"
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                {message.voice_duration && (
                  <span className="text-xs text-[var(--text-secondary)]">
                    {message.voice_duration}s
                  </span>
                )}
                {renderTimestampAndStatus()}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-[var(--text-secondary)]">
              <div className="h-4 w-4 border-2 border-[var(--text-secondary)] rounded-full animate-spin" />
              Loading audio...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 